import React from 'react';
import PropTypes from 'prop-types';
import {
  Page,
  View,
  Text,
} from '@react-pdf/renderer';

import FlagIcon from '../../assets/icons/FlagIcon';

import TablePageHeader from '../../components/TablePageHeader';
import BottomPagination from '../../components/BottomPagination';
import DraftOverlay from '../../components/DraftOverlay';
import { truncateStr } from '../../../../../../utils';

export default function TrnDateVolatility({ reportData, totalPageCount, exhibitNumber }) {
  const tableData = reportData.TransactionDateVolatility;
  
  const selectedVolatility = `${reportData.ValuationDateVolatility?.goalSeekVolatility}%`;

  const selectedVolatilityColumns = Object.keys(tableData?.volatilityObject)
    .filter((key) => key.includes('Volatility - Transaction Date'))
    .sort((a, b) => a - b)
    .map((key) => {
      const year = parseFloat(key.match(/\d+(?:\.\d+)?/)[0]);
      const yearLabel = year === 1 ? 'year' : 'years';
      return `${year} ${yearLabel}`;
    });
  const table1Headers = [
    'Outlier',
    'Company name',
    'LTM Total Revenue',
    ...selectedVolatilityColumns,
    'Weighted equity\nvolatility',
  ];

  function returnCellData(dataName, rowIndex, pageIndex) {
    if (dataName === 'Company name') {
      const name = tableData?.volatilityObject.Name[rowIndex + (pageIndex * 37)];
      return name ? truncateStr(String(name), 36) : '-';
    }
    let valToReturn;
    if (dataName === 'LTM Total Revenue') {
      valToReturn = tableData?.volatilityObject['Revenue | LTM TD'][rowIndex + (pageIndex * 37)];
      return valToReturn ?
        valToReturn.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 1, maximumFractionDigits: 1 }) :
        '-';
    }
    if (dataName.includes('year') || dataName.includes('years')) {
      const yearLabel = dataName.split(' ')[0];
      valToReturn = tableData?.volatilityObject[`Volatility - Transaction Date | ${yearLabel}.0 year`][rowIndex + (pageIndex * 37)];
    }
    if (dataName === 'Weighted equity\nvolatility') {
      valToReturn = tableData?.volatilityObject['Interpolated Volatility'][rowIndex + (pageIndex * 37)] !== '-' ?
        tableData?.volatilityObject['Interpolated Volatility'][rowIndex + (pageIndex * 37)] :
        null;
    }
    return valToReturn || '-';
  }
  // Map statistics to their correct indices in the backend data
  const statToIndexMap = {
    'Maximum': 0,      // max
    '95th percentile': 1, // 95%
    '90th percentile': 2, // 90%
    '75th percentile': 3, // 75%
    'Median': 5,       // 50% (note: this is at index 5, not 4)
    '25th percentile': 4, // 25% (using first occurrence at index 4)
    '10th percentile': 7, // 10%
    '5th percentile': 8,  // 5%
    'Minimum': 9,      // min
    'Average': 10,     // mean
  };

  function returnSummaryData(dataName, rowIndex) {
    // Get the correct index based on the statistic name
    const statName = ['Maximum', '95th percentile', '90th percentile', 
                     '75th percentile', 'Median', '25th percentile',
                     '10th percentile', '5th percentile', 'Minimum', 'Average'][rowIndex];
    
    // Use the mapping to get the correct index in the backend data
    const correctIndex = statToIndexMap[statName];    
    let valToReturn;
    if (dataName === 'LTM Total Revenue') {
      valToReturn = tableData?.summaryObject['Revenue | LTM TD'][correctIndex];
      return valToReturn ?
        valToReturn.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 1, maximumFractionDigits: 1 }) :
        '-';
    }
    if (dataName.includes('year') || dataName.includes('years')) {
      const yearLabel = dataName.split(' ')[0];
      valToReturn = tableData?.summaryObject[`Volatility - Transaction Date | ${yearLabel}.0 year`][correctIndex];
    }
    if (dataName === 'Weighted equity\nvolatility') {
      if (tableData.summaryObject['Interpolated Volatility']) {
        valToReturn = tableData?.summaryObject['Interpolated Volatility'][correctIndex] !== '-' ?
          tableData?.summaryObject['Interpolated Volatility'][correctIndex] :
          null;
      }
      else if (tableData.volatilityObject['Interpolated Volatility'])
        valToReturn = tableData?.volatilityObject['Interpolated Volatility'][correctIndex] !== '-' ?
          tableData?.volatilityObject['Interpolated Volatility'][correctIndex] :
          null;
    }
    return valToReturn || '-';
  }

  const numberOfPages = Math.ceil((tableData?.volatilityObject.Name.length || 0) / 37) || 1;

  // Use optional chaining for footnote
  const footnote = reportData?.footnoteData?.tdVolatilityBacksolve
    || reportData?.footnoteData?.volPercentFn
    || reportData?.footnoteData?.allocationMethodologySummary?.opmDetails?.volatilitySelectionMethodology?.ac
    || '-';

  // Log the summary data for each statistic
  console.log('Transaction Date Volatility - Summary data for statistics:');
  Object.entries(statToIndexMap).forEach(([stat, index]) => {
    console.log(`${stat} (backend index ${index}):`, tableData?.summaryObject['Interpolated Volatility'] ? 
      tableData?.summaryObject['Interpolated Volatility'][index] : 'Not available');
  });

  return (
    <>
      {[...Array(numberOfPages)].map((_, i) => i).map((pageNumber, pageIndex) => (
        <Page
          key={pageNumber}
          size="TABLOID"
          orientation="landscape"
          style={{ position: 'relative', backgroundColor: '#EAEAEB' }}
        >
          <TablePageHeader
            companyName={reportData.companyInfo.companyName}
            pageName={`Transaction Date Volatility, Industry   |   Exhibit ${exhibitNumber}`}
          />
          <View
            style={{
              marginTop: '84px',
              marginLeft: '32px',
              display: 'flex',
              flexDirection: 'column',
              width: '1160px',
              border: '1px',
              borderColor: '#AFB1B3',
            }}
          >
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: '24px',
                backgroundColor: '#0C2617',
              }}
            >
              <Text
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#8AB9A4',
                  paddingLeft: '16px',
                  letterSpacing: '0.08px',
                  width: '60px',
                  paddingTop: '6px',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                  height: '24px',
                }}
              >
                (USD$)
              </Text>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: '24px',
                  width: '300px',
                  marginRight: '8px',
                  paddingLeft: '6px',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                }}
              >
                <Text
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '10px',
                    color: '#8AB9A4',
                    letterSpacing: '0.08px',
                    paddingTop: '6px',
                    height: '24px',
                  }}
                >
                  (
                </Text>
                <View
                  style={{
                    height: '16px',
                    width: '12px',
                    paddingTop: '1px',
                  }}
                >
                  <FlagIcon fillColor="#98B8AA" />
                </View>
                <Text
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '10px',
                    color: '#8AB9A4',
                    letterSpacing: '0.08px',
                    marginRight: '8px',
                    paddingTop: '6px',
                    height: '24px',
                  }}
                >
                  = Outlier)
                </Text>
              </View>
              <Text
                style={{
                  fontFamily: 'RobotoMed',
                  fontSize: '10px',
                  color: '#E1E2EC',
                  paddingLeft: '16px',
                  letterSpacing: '0.08px',
                  width: '800px',
                  paddingTop: '6px',
                  borderBottom: '1px',
                  borderColor: '#CCCCCC',
                  height: '24px',
                }}
              >
                Daily equity volatility
              </Text>
            </View>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                height: '40px',
                backgroundColor: '#307157',
                borderBottom: '1px',
                borderColor: '#CCCCCC',
              }}
            >
              {table1Headers.map((dataName, index) => (
                <View key={dataName}>
                  {dataName === 'Outlier' ? (
                    <View
                      style={{
                        height: '16px',
                        width: '24px',
                        paddingLeft: '10px',
                        marginTop: 'auto',
                      }}
                    >
                      <FlagIcon fillColor="#98B8AA" />
                    </View>
                  ) : (
                    <Text
                      key={dataName}
                      style={{
                        fontFamily: 'RobotoMed',
                        fontSize: '10px',
                        color: '#E1E2EC',
                        width: index === 0 ? '20px' : index === 1 ? '200px' : '137px',
                        marginTop: 'auto',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        letterSpacing: '0.5px',
                        textAlign: index > 1 && 'right',
                        lineHeight: '0.55mm',
                      }}
                    >
                      {dataName}
                      {index === 2 && (
                        <Text
                          key={dataName}
                          style={{
                            color: '#B2C6BE',
                          }}
                        >
                          {'\n$M'}
                        </Text>
                      )}

                    </Text>
                  )}
                </View>
              ))}
            </View>
            {tableData?.volatilityObject.Name.slice(pageIndex * 37, (pageIndex + 1) * 37).map((compName, rowIndex) => (
              <View
                key={`${compName}-${pageIndex * 37 + rowIndex}`}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: '16px',
                  backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                }}
              >
                {table1Headers.map((dataName, cellIndex) => (
                  <View key={dataName}>
                    {cellIndex === 0 ? (
                      <View
                        style={{
                          height: '16px',
                          width: '24px',
                          paddingLeft: '10px',
                          marginTop: '2px',
                        }}
                      >
                        {tableData?.volatilityObject.Outlier[rowIndex + (pageIndex * 37)] === 1 && (
                          <FlagIcon fillColor="#307157" />
                        )}
                      </View>
                    ) : (
                      <Text
                        key={dataName}
                        style={{
                          fontFamily: 'Roboto',
                          fontSize: '10px',
                          color: '#49454F',
                          width: cellIndex === 0 ? '20px' : cellIndex === 1 ? '200px' : '137px',
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          letterSpacing: '0.4px',
                          textAlign: cellIndex > 1 && 'right',
                        }}
                      >
                        {returnCellData(dataName, rowIndex, pageIndex) || '-'}
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            ))}
          </View>
          {(pageIndex === numberOfPages - 1) && (tableData?.volatilityObject.Name.slice(pageIndex * 37, (pageIndex + 1) * 37).length <= 24) && (
            <>
              <View
                style={{
                  marginTop: '16px',
                  marginLeft: '32px',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '1160px',
                  border: '1px',
                  borderColor: '#AFB1B3',
                }}
              >
                {['Maximum', '95th percentile', '90th percentile',
                  '75th percentile', 'Median', '25th percentile',
                  '10th percentile', '5th percentile', 'Minimum', 'Average',
                ].map((rowName, rowIndex) => (
                  <View
                    key={rowName}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      height: '16px',
                      backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                    }}
                  >
                    {table1Headers.map((dataName, cellIndex) => (
                      <Text
                        key={dataName}
                        style={{
                          fontFamily: cellIndex === 0 ? 'RobotoMed' : 'Roboto',
                          fontSize: '10px',
                          color: '#49454F',
                          width: cellIndex === 0 ? '200px' : '137px',
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          letterSpacing: '0.4px',
                          textAlign: cellIndex !== 0 && 'right',
                        }}
                      >
                        {cellIndex === 0 ? rowName : returnSummaryData(dataName, rowIndex)}
                      </Text>
                    ))}
                  </View>
                ))}
              </View>
              <View
                style={{
                  width: '1160px',
                  marginTop: '16px',
                  marginLeft: '32px',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  height: '24px',
                  backgroundColor: '#9ECEBB',
                }}
              >
                <Text
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '14px',
                    color: '#49454F',
                    paddingRight: '16px',
                    letterSpacing: '0.08px',
                  }}
                >
                  Selected equity volatility&nbsp;
                  <Text
                    style={{
                      fontFamily: 'RobotoBold',
                      fontSize: '16px',
                      color: '#000000',
                      letterSpacing: '0.1px',
                    }}
                  >
                    {selectedVolatility}
                  </Text>
                </Text>
              </View>
            </>
          )}
          <BottomPagination
            pageNumber={totalPageCount + pageIndex}
            reportData={reportData}
          />
          {reportData.isDraft && <DraftOverlay />}
        </Page>
      ))}
      {tableData?.volatilityObject.Name.slice((numberOfPages - 1) * 37, numberOfPages * 37).length > 24 && (
        <Page
          size="TABLOID"
          orientation="landscape"
          style={{ position: 'relative', backgroundColor: '#EAEAEB' }}
        >
          <TablePageHeader
            companyName={reportData.companyInfo.companyName}
            pageName={`Transaction Date Volatility, Industry   |   Exhibit ${exhibitNumber}`}
          />
          <View
            style={{
              marginTop: '84px',
              marginLeft: '32px',
              display: 'flex',
              flexDirection: 'column',
              width: '1160px',
              border: '1px',
              borderColor: '#AFB1B3',
            }}
          >
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                height: '40px',
                backgroundColor: '#307157',
                borderBottom: '1px',
                borderColor: '#CCCCCC',
              }}
            >
              {table1Headers.map((dataName, index) => (
                <Text
                  key={dataName}
                  style={{
                    fontFamily: 'RobotoMed',
                    fontSize: '10px',
                    color: '#E1E2EC',
                    width: index > 1 ? '200px' : '137px',
                    marginTop: 'auto',
                    paddingLeft: '16px',
                    paddingRight: index > 2 ? '10px' : '6px',
                    letterSpacing: '0.5px',
                    textAlign: index !== 1 && 'right',
                    lineHeight: '0.55mm',
                  }}
                >
                  {index > 1 && dataName}
                  {index === 2 && (
                    <Text
                      key={dataName}
                      style={{
                        color: '#B2C6BE',
                      }}
                    >
                      {'\n$M'}
                    </Text>
                  )}
                </Text>
              ))}
            </View>
            {['Maximum', '95th percentile', '90th percentile',
              '75th percentile', 'Median', '25th percentile',
              '10th percentile', '5th percentile', 'Minimum', 'Average',
            ].map((rowName, rowIndex) => (
              <View
                key={rowName}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: '16px',
                  backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
                }}
              >
                {table1Headers.map((dataName, cellIndex) => (
                  <Text
                    key={dataName}
                    style={{
                      fontFamily: cellIndex === 0 ? 'RobotoMed' : 'Roboto',
                      fontSize: '10px',
                      color: '#49454F',
                      width: cellIndex === 0 ? '200px' : '137px',
                      display: cellIndex === 1 ? 'none' : 'block',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      letterSpacing: '0.4px',
                      textAlign: cellIndex !== 0 && 'right',
                    }}
                  >
                    {cellIndex === 0 ? rowName : cellIndex === 1 ? '' : returnSummaryData(dataName, rowIndex)}
                  </Text>
                ))}
              </View>
            ))}
          </View>
          <View
            style={{
              width: '1160px',
              marginTop: '16px',
              marginLeft: '32px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
              height: '24px',
              backgroundColor: '#9ECEBB',
            }}
          >
            <Text
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '14px',
                color: '#49454F',
                paddingRight: '16px',
                letterSpacing: '0.08px',
              }}
            >
              Selected equity volatility&nbsp;
              <Text
                style={{
                  fontFamily: 'RobotoBold',
                  fontSize: '16px',
                  color: '#000000',
                  letterSpacing: '0.1px',
                }}
              >
                {selectedVolatility}
              </Text>
            </Text>
          </View>
          {!!footnote && (
            <Text
              style={{
                fontFamily: 'Roboto',
                fontSize: '8px',
                color: '#49454F',
                letterSpacing: '0.4px',
                marginTop: '8px',
                marginLeft: '32px',
                width: 'calc(100% - 64px)',
                lineHeight: '10px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding: '0 8px',
                textAlign: 'left',
              }}
            >
              Footnote: {footnote}
            </Text>
          )}
          <BottomPagination
            pageNumber={totalPageCount + numberOfPages}
            reportData={reportData}
          />
          {reportData.isDraft && <DraftOverlay />}
        </Page>
      )}
    </>
  );
}

TrnDateVolatility.propTypes = {
  reportData: PropTypes.object.isRequired,
  totalPageCount: PropTypes.number.isRequired,
  exhibitNumber: PropTypes.number.isRequired,
};
